<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FormsAPI from 'dashboard/api/forms';
import ProductsAPI from 'dashboard/api/products';
import Drawer from './Drawer.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  form: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const loading = ref(false);
const activeTab = ref('general');
const currentStepIndex = ref(0);
const products = ref([]);

const formData = ref({
  title: '',
  slug: '',
  description: '',
  submit_button_text: 'Envoyer',
  success_message: '',
  redirect_url: '',
  published: false,
  multi_step: false,
  show_progress_bar: true,
  settings: {},
  form_steps_attributes: [
    {
      title: 'Étape 1',
      description: '',
      position: 0,
      button_text: 'Suivant',
      form_fields_attributes: [],
    },
  ],
});

const fieldTypes = [
  { value: 'text', label: t('WEBSITE.FORMS.FIELD_TYPES.TEXT') },
  { value: 'textarea', label: t('WEBSITE.FORMS.FIELD_TYPES.TEXTAREA') },
  { value: 'email', label: t('WEBSITE.FORMS.FIELD_TYPES.EMAIL') },
  { value: 'phone', label: t('WEBSITE.FORMS.FIELD_TYPES.PHONE') },
  { value: 'number', label: t('WEBSITE.FORMS.FIELD_TYPES.NUMBER') },
  { value: 'select', label: t('WEBSITE.FORMS.FIELD_TYPES.SELECT') },
  { value: 'radio', label: t('WEBSITE.FORMS.FIELD_TYPES.RADIO') },
  { value: 'checkbox', label: t('WEBSITE.FORMS.FIELD_TYPES.CHECKBOX') },
  { value: 'date', label: t('WEBSITE.FORMS.FIELD_TYPES.DATE') },
  { value: 'file', label: t('WEBSITE.FORMS.FIELD_TYPES.FILE') },
  { value: 'rating', label: t('WEBSITE.FORMS.FIELD_TYPES.RATING') },
  { value: 'json', label: t('WEBSITE.FORMS.FIELD_TYPES.JSON') },
  { value: 'product_list', label: t('WEBSITE.FORMS.FIELD_TYPES.PRODUCT_LIST') },
];

const tabs = [
  {
    key: 'general',
    label: t('WEBSITE.FORMS.BUILDER.TABS.GENERAL'),
    icon: 'i-lucide-settings',
  },
  {
    key: 'steps',
    label: t('WEBSITE.FORMS.BUILDER.TABS.STEPS'),
    icon: 'i-lucide-list-tree',
  },
];

const isEdit = computed(() => !!props.form);
const modalTitle = computed(() =>
  isEdit.value
    ? t('WEBSITE.FORMS.BUILDER.EDIT_FORM')
    : t('WEBSITE.FORMS.BUILDER.NEW_FORM')
);

const currentStep = computed(
  () => formData.value.form_steps_attributes[currentStepIndex.value]
);

// Charger les données en mode édition
if (isEdit.value && props.form) {
  FormsAPI.show(props.form.id).then(response => {
    const form = response.data;
    formData.value = {
      id: form.id,
      title: form.title || '',
      slug: form.slug || '',
      description: form.description || '',
      submit_button_text: form.submit_button_text || 'Envoyer',
      success_message: form.success_message || '',
      redirect_url: form.redirect_url || '',
      published: form.published || false,
      multi_step: form.multi_step || false,
      show_progress_bar: form.show_progress_bar !== false,
      settings: form.settings || {},
      form_steps_attributes:
        form.steps?.map(step => ({
          id: step.id,
          title: step.title || '',
          description: step.description || '',
          position: step.position || 0,
          button_text: step.button_text || '',
          form_fields_attributes:
            step.fields?.map(field => ({
              id: field.id,
              field_type: field.field_type || 'text',
              label: field.label || '',
              placeholder: field.placeholder || '',
              help_text: field.help_text || '',
              position: field.position || 0,
              required: field.required || false,
              validation_rules: field.validation_rules || {},
              options: field.options || [],
              settings: field.settings || {},
            })) || [],
        })) || [],
    };
  });
}

// Auto-génération du slug
watch(
  () => formData.value.title,
  newTitle => {
    if (!isEdit.value && newTitle) {
      formData.value.slug = newTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  }
);

// Gestion multi-step
watch(
  () => formData.value.multi_step,
  isMultiStep => {
    if (!isMultiStep && formData.value.form_steps_attributes.length > 1) {
      // Fusionner tous les champs dans la première étape
      const allFields = formData.value.form_steps_attributes.flatMap(
        step => step.form_fields_attributes || []
      );
      formData.value.form_steps_attributes = [
        {
          ...formData.value.form_steps_attributes[0],
          title: 'Formulaire',
          form_fields_attributes: allFields,
        },
      ];
      currentStepIndex.value = 0;
    }
  }
);

const addStep = () => {
  const newPosition = formData.value.form_steps_attributes.length;
  formData.value.form_steps_attributes.push({
    title: `Étape ${newPosition + 1}`,
    description: '',
    position: newPosition,
    button_text: 'Suivant',
    form_fields_attributes: [],
  });
  currentStepIndex.value = newPosition;
};

const removeStep = index => {
  if (formData.value.form_steps_attributes.length <= 1) {
    useAlert(t('WEBSITE.FORMS.BUILDER.ERROR.CANNOT_DELETE_LAST_STEP'));
    return;
  }

  const step = formData.value.form_steps_attributes[index];
  if (step.id) {
    // eslint-disable-next-line no-underscore-dangle
    step._destroy = true;
  } else {
    formData.value.form_steps_attributes.splice(index, 1);
  }

  if (currentStepIndex.value >= formData.value.form_steps_attributes.length) {
    currentStepIndex.value = formData.value.form_steps_attributes.length - 1;
  }
};

const addField = () => {
  if (!currentStep.value.form_fields_attributes) {
    currentStep.value.form_fields_attributes = [];
  }

  currentStep.value.form_fields_attributes.push({
    field_type: 'text',
    label: '',
    placeholder: '',
    help_text: '',
    position: currentStep.value.form_fields_attributes.length,
    required: false,
    validation_rules: {},
    options: [],
    settings: {},
  });
};

const removeField = (stepIndex, fieldIndex) => {
  const field =
    formData.value.form_steps_attributes[stepIndex].form_fields_attributes[
      fieldIndex
    ];

  if (field.id) {
    // eslint-disable-next-line no-underscore-dangle
    field._destroy = true;
  } else {
    formData.value.form_steps_attributes[
      stepIndex
    ].form_fields_attributes.splice(fieldIndex, 1);
  }
};

const needsOptions = fieldType => {
  return ['select', 'radio', 'checkbox'].includes(fieldType);
};

const isProductListType = fieldType => {
  return fieldType === 'product_list';
};

// Charger la liste des produits
const fetchProducts = async () => {
  try {
    const response = await ProductsAPI.get();
    products.value = response.data;
  } catch (error) {
    // Ignorer l'erreur silencieusement
  }
};

onMounted(() => {
  fetchProducts();
});

const getOptionsText = field => {
  if (!field.options || !Array.isArray(field.options)) return '';
  return field.options.join('\n');
};

const setOptionsText = (field, value) => {
  field.options = value
    .split('\n')
    .map(opt => opt.trim())
    .filter(opt => opt.length > 0);
};

const handleSubmit = async () => {
  if (!formData.value.title) {
    useAlert(t('COMPONENTS.FORM.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  loading.value = true;

  try {
    // Nettoyer les étapes marquées pour suppression
    const cleanedData = {
      ...formData.value,
      form_steps_attributes: formData.value.form_steps_attributes
        // eslint-disable-next-line no-underscore-dangle
        .filter(step => !step._destroy)
        .map((step, index) => ({
          ...step,
          position: index,
          form_fields_attributes: (step.form_fields_attributes || [])
            // eslint-disable-next-line no-underscore-dangle
            .filter(field => !field._destroy)
            .map((field, fieldIndex) => ({
              ...field,
              position: fieldIndex,
            })),
        })),
    };

    if (isEdit.value) {
      await FormsAPI.update(props.form.id, { form: cleanedData });
      useAlert(t('WEBSITE.FORMS.API.SUCCESS.FORM_UPDATED'));
    } else {
      await FormsAPI.create({ form: cleanedData });
      useAlert(t('WEBSITE.FORMS.API.SUCCESS.FORM_CREATED'));
    }

    emit('save');
  } catch (error) {
    const errorMessage = isEdit.value
      ? t('WEBSITE.FORMS.API.ERROR.UPDATE_FAILED')
      : t('WEBSITE.FORMS.API.ERROR.CREATE_FAILED');
    useAlert(errorMessage);
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Drawer show size="xl" @close="handleClose">
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ modalTitle }}
        </h2>
        <button
          class="p-2 rounded-lg hover:bg-n-alpha-2 transition-colors"
          @click="handleClose"
        >
          <span class="i-lucide-x size-5 text-n-slate-11" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex gap-1 px-6 pt-4 border-b border-n-weak">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors"
          :class="[
            activeTab === tab.key
              ? 'bg-n-solid-1 text-n-blue-11 border-b-2 border-n-blue-9'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1',
          ]"
          @click="activeTab = tab.key"
        >
          <span :class="tab.icon" class="size-4" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Tab: General -->
        <div v-show="activeTab === 'general'" class="space-y-4">
          <!-- Titre -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.FORMS.BUILDER.TITLE') }}
              <span class="text-n-red-9">*</span>
            </label>
            <Input
              v-model="formData.title"
              :placeholder="t('WEBSITE.FORMS.BUILDER.TITLE_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Slug -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.FORMS.BUILDER.SLUG') }}
            </label>
            <Input
              v-model="formData.slug"
              :placeholder="t('WEBSITE.FORMS.BUILDER.SLUG_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.FORMS.BUILDER.DESCRIPTION') }}
            </label>
            <Textarea
              v-model="formData.description"
              :rows="3"
              :placeholder="t('WEBSITE.FORMS.BUILDER.DESCRIPTION_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Options -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="flex items-center gap-2">
                <Checkbox v-model="formData.multi_step" />
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('WEBSITE.FORMS.BUILDER.MULTI_STEP') }}
                </span>
              </label>
            </div>
            <div v-if="formData.multi_step">
              <label class="flex items-center gap-2">
                <Checkbox v-model="formData.show_progress_bar" />
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('WEBSITE.FORMS.BUILDER.SHOW_PROGRESS') }}
                </span>
              </label>
            </div>
          </div>

          <!-- Textes des boutons -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.FORMS.BUILDER.SUBMIT_BUTTON') }}
              </label>
              <Input
                v-model="formData.submit_button_text"
                :placeholder="t('WEBSITE.FORMS.BUILDER.SUBMIT_PLACEHOLDER')"
                class="w-full"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.FORMS.BUILDER.SUCCESS_MESSAGE') }}
              </label>
              <Input
                v-model="formData.success_message"
                :placeholder="t('WEBSITE.FORMS.BUILDER.SUCCESS_PLACEHOLDER')"
                class="w-full"
              />
            </div>
          </div>

          <!-- URL de redirection -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.FORMS.BUILDER.REDIRECT_URL') }}
            </label>
            <Input
              v-model="formData.redirect_url"
              :placeholder="t('WEBSITE.FORMS.BUILDER.REDIRECT_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Publié -->
          <div>
            <label class="flex items-center gap-2">
              <Checkbox v-model="formData.published" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('WEBSITE.FORMS.BUILDER.PUBLISHED') }}
              </span>
            </label>
          </div>
        </div>

        <!-- Tab: Steps & Fields -->
        <div v-show="activeTab === 'steps'" class="space-y-4">
          <!-- Navigation des étapes (si multi-step) -->
          <div
            v-if="formData.multi_step"
            class="flex items-center gap-2 pb-4 border-b border-n-weak"
          >
            <button
              v-for="(step, index) in formData.form_steps_attributes.filter(
                s => !s._destroy
              )"
              :key="index"
              class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
              :class="[
                currentStepIndex === index
                  ? 'bg-n-blue-3 text-n-blue-11'
                  : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
              ]"
              @click="currentStepIndex = index"
            >
              {{ step.title }}
            </button>
            <button
              class="px-3 py-1.5 text-sm font-medium rounded-lg bg-n-alpha-1 text-n-slate-11 hover:bg-n-purple-3 hover:text-n-purple-11 transition-colors"
              @click="addStep"
            >
              <span class="i-lucide-plus size-4" />
            </button>
          </div>

          <!-- Configuration de l'étape courante -->
          <div class="space-y-4 p-4 rounded-lg bg-n-alpha-1">
            <div class="flex items-center justify-between">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('WEBSITE.FORMS.BUILDER.STEP_CONFIG') }}
              </h3>
              <button
                v-if="
                  formData.multi_step &&
                  formData.form_steps_attributes.filter(s => !s._destroy)
                    .length > 1
                "
                class="p-2 rounded-lg bg-n-ruby-2 hover:bg-n-ruby-3 text-n-ruby-11 transition-colors"
                @click="removeStep(currentStepIndex)"
              >
                <span class="i-lucide-trash-2 size-4" />
              </button>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.FORMS.BUILDER.STEP_TITLE') }}
                </label>
                <Input
                  v-model="currentStep.title"
                  :placeholder="
                    t('WEBSITE.FORMS.BUILDER.STEP_TITLE_PLACEHOLDER')
                  "
                  class="w-full"
                />
              </div>
              <div v-if="formData.multi_step">
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.FORMS.BUILDER.STEP_BUTTON') }}
                </label>
                <Input
                  v-model="currentStep.button_text"
                  :placeholder="
                    t('WEBSITE.FORMS.BUILDER.STEP_BUTTON_PLACEHOLDER')
                  "
                  class="w-full"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.FORMS.BUILDER.STEP_DESCRIPTION') }}
              </label>
              <Textarea
                v-model="currentStep.description"
                :rows="2"
                :placeholder="
                  t('WEBSITE.FORMS.BUILDER.STEP_DESCRIPTION_PLACEHOLDER')
                "
                class="w-full"
              />
            </div>
          </div>

          <!-- Champs du formulaire -->
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('WEBSITE.FORMS.BUILDER.FIELDS') }}
              </h3>
              <Button
                :label="t('WEBSITE.FORMS.BUILDER.ADD_FIELD')"
                icon="i-lucide-plus"
                size="sm"
                @click="addField"
              />
            </div>

            <!-- Liste des champs -->
            <div
              v-if="
                currentStep.form_fields_attributes &&
                currentStep.form_fields_attributes.filter(f => !f._destroy)
                  .length > 0
              "
              class="space-y-3"
            >
              <div
                v-for="(
                  field, fieldIndex
                ) in currentStep.form_fields_attributes.filter(
                  f => !f._destroy
                )"
                :key="fieldIndex"
                class="p-4 rounded-lg border border-n-weak bg-n-solid-1"
              >
                <div class="flex items-start justify-between mb-3">
                  <span class="text-xs font-semibold text-n-slate-11 uppercase">
                    {{ t('WEBSITE.FORMS.BUILDER.FIELD') }} #{{ fieldIndex + 1 }}
                  </span>
                  <button
                    class="p-1 rounded hover:bg-n-ruby-2 text-n-slate-11 hover:text-n-ruby-11 transition-colors"
                    @click="removeField(currentStepIndex, fieldIndex)"
                  >
                    <span class="i-lucide-x size-4" />
                  </button>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <!-- Type de champ -->
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{ t('WEBSITE.FORMS.BUILDER.FIELD_TYPE') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="field.field_type"
                        class="block w-full h-8 !px-3 !py-2 text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand transition-all duration-500 ease-in-out cursor-pointer pr-10 [appearance:none] [-webkit-appearance:none] [-moz-appearance:none]"
                      >
                        <option
                          v-for="type in fieldTypes"
                          :key="type.value"
                          :value="type.value"
                        >
                          {{ type.label }}
                        </option>
                      </select>
                      <span
                        class="absolute right-3 top-1/2 -translate-y-1/2 i-lucide-chevron-down size-4 text-n-slate-11 pointer-events-none"
                      />
                    </div>
                  </div>

                  <!-- Label -->
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{ t('WEBSITE.FORMS.BUILDER.FIELD_LABEL') }}
                    </label>
                    <Input
                      v-model="field.label"
                      :placeholder="
                        t('WEBSITE.FORMS.BUILDER.FIELD_LABEL_PLACEHOLDER')
                      "
                      class="w-full"
                      size="sm"
                    />
                  </div>

                  <!-- Placeholder -->
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{ t('WEBSITE.FORMS.BUILDER.FIELD_PLACEHOLDER') }}
                    </label>
                    <Input
                      v-model="field.placeholder"
                      :placeholder="
                        t('WEBSITE.FORMS.BUILDER.FIELD_PLACEHOLDER_PLACEHOLDER')
                      "
                      class="w-full"
                      size="sm"
                    />
                  </div>

                  <!-- Help text -->
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{ t('WEBSITE.FORMS.BUILDER.FIELD_HELP') }}
                    </label>
                    <Input
                      v-model="field.help_text"
                      :placeholder="
                        t('WEBSITE.FORMS.BUILDER.FIELD_HELP_PLACEHOLDER')
                      "
                      class="w-full"
                      size="sm"
                    />
                  </div>
                </div>

                <!-- Options (pour select, radio, checkbox) -->
                <div v-if="needsOptions(field.field_type)" class="mt-3">
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    {{ t('WEBSITE.FORMS.BUILDER.FIELD_OPTIONS') }}
                  </label>
                  <Textarea
                    :model-value="getOptionsText(field)"
                    :rows="4"
                    :placeholder="
                      t('WEBSITE.FORMS.BUILDER.FIELD_OPTIONS_PLACEHOLDER')
                    "
                    class="w-full font-mono text-xs"
                    @update:model-value="setOptionsText(field, $event)"
                  />
                </div>

                <!-- Liste de produits (pour product_list) -->
                <div
                  v-if="isProductListType(field.field_type)"
                  class="mt-3 p-3 rounded-lg bg-n-alpha-1 max-h-64 overflow-y-auto"
                >
                  <label class="block text-xs font-medium text-n-slate-11 mb-2">
                    {{ t('WEBSITE.FORMS.BUILDER.SELECT_PRODUCTS') }}
                  </label>
                  <div v-if="products.length > 0" class="space-y-2">
                    <label
                      v-for="product in products"
                      :key="product.id"
                      class="flex items-center gap-2 p-2 rounded hover:bg-n-alpha-2 cursor-pointer transition-colors"
                    >
                      <input
                        :checked="field.options?.includes(product.id)"
                        type="checkbox"
                        class="w-4 h-4 rounded border-n-weak text-n-blue-9 focus:ring-2 focus:ring-n-blue-9 cursor-pointer"
                        @change="
                          e => {
                            if (!field.options) field.options = [];
                            if (e.target.checked) {
                              if (!field.options.includes(product.id)) {
                                field.options.push(product.id);
                              }
                            } else {
                              field.options = field.options.filter(
                                id => id !== product.id
                              );
                            }
                          }
                        "
                      />
                      <div class="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          v-if="product.images && product.images.length > 0"
                          class="w-8 h-8 rounded overflow-hidden flex-shrink-0"
                        >
                          <img
                            :src="product.images[0].url"
                            :alt="product.name"
                            class="w-full h-full object-cover"
                          />
                        </div>
                        <div
                          v-else
                          class="w-8 h-8 rounded bg-n-alpha-1 flex items-center justify-center flex-shrink-0"
                        >
                          <span
                            class="i-lucide-package size-4 text-n-slate-10"
                          />
                        </div>
                        <span
                          class="text-xs font-medium text-n-slate-12 truncate"
                        >
                          {{ product.name }}
                        </span>
                        <span class="text-xs text-n-slate-10">
                          ({{ product.sku }})
                        </span>
                      </div>
                    </label>
                  </div>
                  <p v-else class="text-xs text-n-slate-10 text-center py-4">
                    {{ t('WEBSITE.FORMS.BUILDER.NO_PRODUCTS') }}
                  </p>
                </div>

                <!-- Requis -->
                <div class="mt-3">
                  <label class="flex items-center gap-2">
                    <Checkbox v-model="field.required" />
                    <span class="text-xs font-medium text-n-slate-12">
                      {{ t('WEBSITE.FORMS.BUILDER.FIELD_REQUIRED') }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div
              v-else
              class="flex flex-col items-center justify-center py-12 rounded-lg bg-n-alpha-1"
            >
              <span class="i-lucide-inbox size-12 text-n-slate-10 mb-3" />
              <p class="text-sm text-n-slate-11">
                {{ t('WEBSITE.FORMS.BUILDER.NO_FIELDS') }}
              </p>
              <Button
                :label="t('WEBSITE.FORMS.BUILDER.ADD_FIRST_FIELD')"
                icon="i-lucide-plus"
                size="sm"
                class="mt-3"
                @click="addField"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 p-6 border-t border-n-weak"
      >
        <Button
          :label="t('WEBSITE.FORMS.BUILDER.CANCEL')"
          color="slate"
          variant="outline"
          @click="handleClose"
        />
        <Button
          :label="t('WEBSITE.FORMS.BUILDER.SAVE')"
          :loading="loading"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Drawer>
</template>
